import { LineChart, type LineSeries } from '../charts/LineChart.js'
import { StackedBars } from '../charts/StackedBars.js'
import { INK, MARINE, SEQUENTIAL } from '../charts/chartColors.js'
import { EMPLOYMENT_LABELS, EMPLOYMENT_ORDER, latestByFacility, rateByPeriod, sumByPeriod } from '../insightsSeries.js'
import { monthLabel } from '../labels.js'
import type { FacilityMonthRollup, PortfolioRow } from '../types.js'

const two = (value: number): string => value.toFixed(2)
const whole = (value: number): string => Math.round(value).toLocaleString('en-US')
const EMPLOYMENT_COLORS: Record<(typeof EMPLOYMENT_ORDER)[number], string> = { full_time: '#08404F', part_time: '#4F93A8', prn: '#9CC5D2', agency: '#C8A84B' }

/**
 * Census and workforce (plan section 6.6): hours per patient day scheduled, worked and target by month; the recorded
 * patient days against the plan's; the roster by employment type at each month's end with the hires and leavers; and
 * each facility's FTE gap in its latest month. Retention and the staffed-days calendar are the facility's own reports.
 */
export function CensusScreen({ rollups, periods, rows }: { rollups: readonly FacilityMonthRollup[]; periods: readonly string[]; rows: readonly PortfolioRow[] }) {
  const withCensus = rollups.filter((rollup) => rollup.census.ppd > 0)
  const hppd: LineSeries[] = [
    { key: 'worked', name: 'Worked', color: MARINE, values: rateByPeriod(withCensus, periods, (rollup) => rollup.staffing.hoursWorked, (rollup) => rollup.census.ppd, 1, 2) },
    { key: 'scheduled', name: 'Scheduled', color: SEQUENTIAL[1] ?? INK, values: rateByPeriod(withCensus, periods, (rollup) => rollup.staffing.hoursScheduled, (rollup) => rollup.census.ppd, 1, 2) },
    { key: 'target', name: 'Target', color: INK, dashed: true, values: rateByPeriod(withCensus, periods, (rollup) => rollup.staffing.hoursTarget, (rollup) => rollup.census.ppd, 1, 2) },
  ]
  const census: LineSeries[] = [
    { key: 'ppd', name: 'Patient days recorded', color: MARINE, values: sumByPeriod(withCensus, periods, (rollup) => rollup.census.ppd) },
    { key: 'planned', name: 'Planned', color: INK, dashed: true, values: sumByPeriod(withCensus, periods, (rollup) => rollup.census.plannedPpd) },
  ]
  const headcount = EMPLOYMENT_ORDER.map((type) => ({ key: type, name: EMPLOYMENT_LABELS[type], color: EMPLOYMENT_COLORS[type], values: sumByPeriod(rollups, periods, (rollup) => rollup.workforce.headcount[type]) }))
  const hired = sumByPeriod(rollups, periods, (rollup) => rollup.workforce.hired)
  const left = sumByPeriod(rollups, periods, (rollup) => rollup.workforce.left)
  const latest = latestByFacility(rollups)
  const fte = rows
    .map((row) => ({ row, rollup: latest.get(row.facilityId) ?? null }))
    .filter((entry): entry is { row: PortfolioRow; rollup: FacilityMonthRollup } => entry.rollup !== null)
    .map(({ row, rollup }) => ({ row, period: rollup.period, needed: rollup.workforce.fteWithBuffer, onRoster: rollup.workforce.fteOnRoster, gap: Math.round((rollup.workforce.fteOnRoster - rollup.workforce.fteWithBuffer) * 10) / 10 }))
    .sort((a, b) => a.gap - b.gap)
  const hiredTotal = hired.reduce<number>((sum, value) => sum + (value ?? 0), 0)
  const leftTotal = left.reduce<number>((sum, value) => sum + (value ?? 0), 0)

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <section className="flex min-w-0 flex-col gap-3 rounded-lg border border-marine/15 bg-white px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="flex-1 text-sm font-black text-ink">Hours per patient day</h2>
            <span className="text-[11px] text-ink-mid">Productive hours over patient days, the facilities with a census</span>
          </div>
          {withCensus.length === 0 ? (
            <div className="py-6 text-center text-xs text-ink-mid">No facility recorded a census in this span.</div>
          ) : (
            <>
              <LineChart periods={periods} series={hppd} format={two} periodLabel={monthLabel} title="Hours per patient day scheduled, worked and target by month" height={240} />
              <Legend series={hppd} />
            </>
          )}
        </section>
        <section className="flex min-w-0 flex-col gap-3 rounded-lg border border-marine/15 bg-white px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="flex-1 text-sm font-black text-ink">Census against the plan</h2>
            <span className="text-[11px] text-ink-mid">Patient days recorded, and the planned census over the same days</span>
          </div>
          {withCensus.length === 0 ? (
            <div className="py-6 text-center text-xs text-ink-mid">No facility recorded a census in this span.</div>
          ) : (
            <>
              <LineChart periods={periods} series={census} format={whole} periodLabel={monthLabel} title="Patient days recorded against the plan by month" height={240} />
              <Legend series={census} />
            </>
          )}
        </section>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="flex min-w-0 flex-col gap-3 rounded-lg border border-marine/15 bg-white px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="flex-1 text-sm font-black text-ink">Headcount by employment type</h2>
            <span className="text-[11px] text-ink-mid">
              At each month's end · {hiredTotal} hired, {leftTotal} left in the span
            </span>
          </div>
          <StackedBars periods={periods} layers={headcount} periodLabel={monthLabel} format={whole} title="Headcount by employment type at each month's end" height={240} />
          <div className="flex flex-wrap gap-3 text-[11px] text-ink-mid">
            {headcount.map((layer) => (
              <span key={layer.key} className="inline-flex items-center gap-1.5">
                <span className="inline-block h-3 w-3 rounded-sm" style={{ background: layer.color }} />
                {layer.name}
              </span>
            ))}
          </div>
        </section>
        <section className="flex min-w-0 flex-col gap-3 rounded-lg border border-marine/15 bg-white px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="flex-1 text-sm font-black text-ink">The FTE gap</h2>
            <span className="text-[11px] text-ink-mid">The workforce plan's FTEs with its buffer against the roster's, each facility's latest month</span>
          </div>
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-left text-[10px] font-bold uppercase tracking-wide text-ink-mid">
                <th className="py-1">Facility</th>
                <th className="py-1 text-right">Needed</th>
                <th className="py-1 text-right">On roster</th>
                <th className="py-1 text-right">Gap</th>
              </tr>
            </thead>
            <tbody>
              {fte.map((entry) => (
                <tr key={entry.row.facilityId} className="border-t border-marine/10">
                  <td className="py-1 text-ink">
                    {entry.row.name} <span className="text-ink-mid">· {monthLabel(entry.period)}</span>
                  </td>
                  <td className="py-1 text-right">{entry.needed.toFixed(1)}</td>
                  <td className="py-1 text-right">{entry.onRoster.toFixed(1)}</td>
                  <td className="py-1 text-right font-bold" style={{ color: entry.gap < 0 ? '#D9553F' : '#2E8B57' }}>
                    {entry.gap > 0 ? '+' : ''}
                    {entry.gap.toFixed(1)}
                  </td>
                </tr>
              ))}
              {fte.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-4 text-center text-ink-mid">
                    No month counted yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <p className="text-[10px] text-ink-mid">A negative gap is fewer FTEs than the plan with its buffer asks for. Retention by month and the staffed-days calendar are on each facility's Reporting tab.</p>
        </section>
      </div>
    </div>
  )
}

function Legend({ series }: { series: readonly LineSeries[] }) {
  return (
    <div className="flex flex-wrap gap-3 text-[11px] text-ink-mid">
      {series.map((entry) => (
        <span key={entry.key} className="inline-flex items-center gap-1.5">
          <span className="inline-block h-0 w-3.5" style={{ borderTop: `2px ${entry.dashed ? 'dashed' : 'solid'} ${entry.color}` }} />
          {entry.name}
        </span>
      ))}
    </div>
  )
}
